import { useCallback, useState } from "react";
import { usePackageWithLeaguesCreationService } from "@services/domains/packages";
import type { SubjectConfiguration } from "../../types";

export class InferLinkCodeError extends Error {
  courseId: string;

  constructor(courseId: string, cause?: unknown) {
    super(`Could not infer a league code for course ${courseId}`, { cause });
    this.name = "InferLinkCodeError";
    this.courseId = courseId;
  }
}

interface UseInferLinkCodesProps {
  scenarioId: string;
  originId: string;
}

export const useInferLinkCodes = ({ scenarioId, originId }: UseInferLinkCodesProps) => {
  const { inferLinkCode } = usePackageWithLeaguesCreationService();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<InferLinkCodeError | null>(null);

  const inferLinkCodes = useCallback(
    async (configurations: SubjectConfiguration[]) => {
      const createNewConfigurations = configurations.filter(
        configuration => configuration.choice.kind === "create-new",
      );
      setIsLoading(true);
      setError(null);

      try {
        const entries = await Promise.all(
          createNewConfigurations.map(async configuration => {
            const courseId = configuration.subject.courseId;

            try {
              const code = await inferLinkCode(scenarioId, originId, courseId);
              if (!code) throw new InferLinkCodeError(courseId);

              return [courseId, code] as const;
            } catch (mutationError) {
              if (mutationError instanceof InferLinkCodeError) throw mutationError;
              throw new InferLinkCodeError(courseId, mutationError);
            }
          }),
        );

        return Object.fromEntries(entries) as Record<string, string>;
      } catch (inferenceError) {
        const normalizedError =
          inferenceError instanceof InferLinkCodeError
            ? inferenceError
            : new InferLinkCodeError("unknown", inferenceError);
        setError(normalizedError);
        throw normalizedError;
      } finally {
        setIsLoading(false);
      }
    },
    [inferLinkCode, originId, scenarioId],
  );

  return { inferLinkCodes, isLoading, error };
};

export default useInferLinkCodes;
